import { ChangeConfig } from './changeConfig'
import { DisclosureConfig } from './disclosureConfig'
import { AdjustmentInputConfig, InputConfig } from './formBuilder'
import { ProductCondition } from './inputConditions'
import { ManagementConfig } from './management'

export interface ConfigurationState {
  config: StateConfig
}

export enum DocumentType {
  PRODUCT = 'product',
  POLICY = 'policy',
  STATIC = 'static',
}

interface Document {
  id: string
  type: DocumentType
}

export interface ProductDocument extends Document {
  type: DocumentType.PRODUCT
  coverCode?: string
}

export interface PolicyDocument extends Document {
  type: DocumentType.POLICY
}

export interface StaticDocument extends Document {
  type: DocumentType.STATIC
}

export type DocumentConfig = ProductDocument | PolicyDocument | StaticDocument

export type VRNFormatConfig = {
  divider: string
  regex: string
  regexBlacklist?: string
  regexFormat: string[]
  regexIncomplete: string
}

export type FormatsConfig = {
  phoneNumber: Record<'prefix' | 'regex', string>
  postalCode: Record<'regex', string>
  stripeToken: Record<'regex', string>
  vehicleRegistration: VRNFormatConfig
}

export type Provider = string

interface StripeConfig {
  apiKey: string
  stripeAccount: string | undefined
}

export interface TwilioConfig {
  chatServer: string
}

export interface Localisation {
  locale: string
  currency: string
  timeZone: string
  fallbackLanguage: string
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
export interface Config {
  localisation?: Partial<Localisation>
  sessionClient: any
  apiClient: any
  app: any
  auth0: any
  state: any
  stripe: StripeConfig
  airbrake: any // Actually AirbrakeConfig
  logLevel: string
  twilio?: Partial<TwilioConfig>
}

/* eslint-enable  @typescript-eslint/no-explicit-any */

export interface AppConfig {
  assetDomain: string
  cookieDomain: string
  auth0Domain: string
  provider: Provider
  title: string
  description: string
  themeColor: string
}

export interface Scheme {
  schemeCode: string
  productUrlName: string
  hidden?: boolean
  disabled?: boolean
}

export interface RetrieveConfig {
  backBooks?: string[]
}

export interface FeatureToggles {
  // Long-running toggles
  allowModifyBillingDay: boolean
  isAdmin: boolean
  multiQuote: boolean
  disableBind: boolean
  axitechFnol: boolean
}

export interface StateConfig {
  keys?: {
    maps?: string
  }
  formats: FormatsConfig
  productConfig: Record<string, ProductConfig>
  contentBaseUrl: string
  provider: Provider
  schemes: Scheme[]
  features: Partial<FeatureToggles>
  inputs: Record<string, InputConfig>
  adjustments: Record<string, ChangeConfig>
  account?: InputConfig[]
  proposalTracking?: string[]
  createProposal?: {
    lookups?: Record<string, string>
    passthroughs?: string[]
  }
  quoteData?: string[]
  retrieve?: RetrieveConfig
  footer?: string[]
}

export enum ProductKind {
  SUBSCRIPTION = 'SUBSCRIPTION',
  ANNUAL = 'ANNUAL',
  DRIVEAWAY = 'DRIVEAWAY',
}

export interface ProposalConfirmationConfig {
  conditions: ProductCondition[]
  inputs: AdjustmentInputConfig[]
}

export interface FeedbackConfig {
  type: string
  actions: Record<string, string>
}

export type UploadsConfig = Record<
  string,
  Partial<{
    maxSize: number
    maxFiles: number
    accept: { [key: string]: string[] }
  }>
>

export interface ExcessConfig {
  voluntaryExcess?: number[]
  isAdjustable?: boolean
  endorsements: string[]
}

export enum CancellationType {
  Standard = 'Standard',
  CoolingOff = 'CoolingOff',
}

export enum CoolingOffStart {
  Inception = 'Inception',
  Bind = 'Bind',
}

export interface CancellationCoolingOffConfig {
  type: CancellationType.CoolingOff
  reasons?: string[]
  startAt: CoolingOffStart
  days: number
}

export interface CancellationStandardConfig {
  type: CancellationType.Standard
  reasons?: string[]
  daysToDefer?: number
}

export type CancellationConfig = CancellationCoolingOffConfig | CancellationStandardConfig

export interface RenewalConfig {
  reasons: string[]
  sections: ManagementConfig[]
}

export interface ProductConfig {
  productKind: ProductKind
  assetTypes: AssetType[]
  endorsements?: string[]
  excess?: ExcessConfig
  cancellation?: CancellationConfig
  documents: DocumentConfig[]
  uploads?: UploadsConfig
  howItWorks?: HowItWorksSection[]
  checkoutChecklist?: string[]
  startAtDayDelay?: number
  disclosures: DisclosureConfig[]
  proposalConfirmation?: ProposalConfirmationConfig
  feedback?: Record<string, FeedbackConfig>
  management: ManagementConfig[]
  summary: ManagementConfig[]
  renewal: RenewalConfig
  accountSetup: AdjustmentInputConfig[]
  expiredProposal: AdjustmentInputConfig[]
  beforeWeBegin?: AdjustmentInputConfig[]
  hideBillingSidebar?: boolean
}

export interface OptionalCoverConfig {
  code: string
  inputs?: AdjustmentInputConfig[]
}

export interface AssetType {
  code: string
  core?: boolean
  covers: {
    subheader?: boolean
    withInfo?: boolean
    included: string[]
    optional?: OptionalCoverConfig[]
  }
}

export interface HowItWorksSection {
  id: string
  illusId: string
}
